import { useRef, useState } from "react";
import ReactSelect from 'react-select';
import { IMaskInput } from 'react-imask';
import { CustomSelectStyle } from './CustomSelectStyle';
import StatusPill from './StatusPill';
import Img from "../Pages/User/components/Img";
import { patients_attachments_url } from "./ImageUrls";
import { EyeIcon, Trash2Icon } from "lucide-react";
import CreatableSelect from "react-select/creatable"
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

const ALLOWED_ATTACHMENT_TYPES = {
  image: ["jpg", "jpeg", "png", "webp", "gif"],
  pdf: ["pdf"],
  word: ["doc", "docx"],
  excel: ["xls", "xlsx", "csv"],
};

const getFileKind = (file) => {
  const name = (file?.name || file?.attachment_file || "").toLowerCase();
  const ext = name.split(".").pop() || "";
  if (ALLOWED_ATTACHMENT_TYPES.image.includes(ext)) return "image";
  if (ALLOWED_ATTACHMENT_TYPES.pdf.includes(ext)) return "pdf";
  if (ALLOWED_ATTACHMENT_TYPES.word.includes(ext)) return "word";
  if (ALLOWED_ATTACHMENT_TYPES.excel.includes(ext)) return "excel";
  return "other";
};

const AttachmentIcon = ({ kind }) => {
  const common = "h-10 w-10 rounded-xl flex items-center justify-center border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/90 text-gray-600 dark:text-gray-200";

  if (kind === "image") {
    return <div className={common}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5"><rect x="3" y="3" width="18" height="18" rx="4" /><path d="M8 15l3-3 3 2 2-3 2 5" /></svg></div>;
  }

  if (kind === "pdf") {
    return <div className={`${common} text-red-500 dark:text-red-400`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5"><path d="M7 3h6l4 4v14H7z" /><path d="M13 3v5h5" /><path d="M9 13h6" /><path d="M9 17h4" /></svg></div>;
  }

  if (kind === "word") {
    return <div className={`${common} text-blue-500 dark:text-blue-400`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 13h6" /><path d="M9 17h4" /></svg></div>;
  }

  if (kind === "excel") {
    return <div className={`${common} text-emerald-500 dark:text-emerald-400`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M8 13h8" /><path d="M8 17h8" /><path d="M12 13v8" /></svg></div>;
  }

  return <div className={`${common} text-gray-500 dark:text-gray-300`}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /></svg></div>;
};

export const FormInput = ({
  label,
  name,
  value,
  readonly = "",
  isMulti = false,
  onChange,
  placeholder = "",
  type = "text",
  required = false,
  rows = 1,
  isRequired = false,
  isDateRange = false,
  className = "",
  error = "",
  disabled = false,
  addMasking = false,
  maskFor = "",
  ...props
}) => {
  const isTextarea = type === 'textarea' || rows > 1;
  const isTags = type === "tags" || rows > 1;
  const maskPattern = addMasking
    ? maskFor === "cnic"
      ? "00000-0000000-0"
      : maskFor === "mobile"
        ? "0000-0000000"
        : undefined
    : undefined;

  const handleChange = (e) => {
    onChange?.(e.target.value);
  };

  const inputClass = `border rounded px-3 py-1.5 w-full bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 transition-all duration-300
    ${error
      ? "border-red-400 dark:border-red-500 focus:ring-red-300 dark:focus:ring-red-500"
      : "border-gray-300 dark:border-gray-600 focus:ring-green-300 dark:focus:ring-green-400"}`;

  return (
    <div className={className}>
      <label className="block font-medium mb-1 text-gray-700 dark:text-gray-200">
        {label}
        {isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
      </label>

      {isTextarea ? (
        <textarea
          name={name}
          readOnly={readonly}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          required={required}
          className={inputClass}
          {...props}
        />
      ) : addMasking && maskPattern ? (
        <IMaskInput
          mask={maskPattern}
          definitions={{ 0: /\d/ }}
          value={value || ""}
          onAccept={(val) => onChange?.(val)}
          overwrite={false}
          disabled={disabled}
          lazy={false}
          readOnly={readonly}
          placeholder={placeholder}
          required={required}
          className={inputClass}
          {...props}
        />
      ) : isTags ? (
        <CreatableSelect
          isMulti={isMulti}
          styles={CustomSelectStyle}
          value={value || []}
          disabled={disabled}
          onChange={(selected) => onChange?.(selected)}
          placeholder={placeholder || "Type and press Enter..."}
        />
      ) : isDateRange ? ( <div className="custom-date-range"><DateRangePicker
        onChange={onChange}
        value={value}
        disabled={disabled}
        clearIcon={null}
        calendarIcon={null}
      /></div>) : (
        <input
          type={type}
          name={name}
          value={value}
          disabled={disabled}
          readOnly={readonly}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          className={inputClass}
          {...props}
        />
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const FormImage = ({
  label = "Upload image",
  initialPreviewUrl = null,
  onChange,
  required = false,
  isRequired = false,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(initialPreviewUrl);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);

  const validate = (f) => {
    if (required && !f && !initialPreviewUrl) {
      setError("Image is required.");
      return false;
    }
    setError("");
    return true;
  };

  const processFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      onChange?.(null, false);
      return;
    }
    if (previewUrl && previewUrl !== initialPreviewUrl) URL.revokeObjectURL(previewUrl);
    const url = URL.createObjectURL(f);
    setFile(f);
    setPreviewUrl(url);
    setError("");
    onChange?.(f, true);
  };

  const handleInputChange = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (previewUrl && previewUrl !== initialPreviewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl(null);
    if (inputRef.current) inputRef.current.value = "";
    const valid = validate(null);
    onChange?.(null, valid);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">
          {label}
          {required || isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
        </label>
      )}

      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200 overflow-hidden
          ${dragging
            ? "border-green-400 bg-green-50 dark:bg-green-500/10 scale-[1.01]"
            : error
              ? "border-red-400 dark:border-red-500 bg-red-50/40 dark:bg-red-500/5"
              : "border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/60 hover:border-green-300 dark:hover:border-green-500/50 hover:bg-green-50/40 dark:hover:bg-green-500/5"
          }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleInputChange}
          className="hidden"
        />

        {previewUrl ? (
          <div className="flex items-center gap-4 p-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-green-200 dark:ring-green-500/30">
              <Img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{file?.name ?? "Current image"}</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Click to replace</p>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="flex-shrink-0 p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 transition"
              title="Remove"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 gap-2 select-none">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-colors ${dragging ? "bg-green-100 dark:bg-green-500/20" : "bg-gray-100 dark:bg-slate-700"}`}>
              <svg className={`w-5 h-5 transition-colors ${dragging ? "text-green-500" : "text-gray-400 dark:text-gray-500"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {dragging ? "Drop to upload" : "Drag & drop or click to upload"}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG, WEBP, GIF up to any size</p>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const MultiDocumentSelect = ({
  label = "Attachments",
  files = [],
  isForDetail = false,
  attachment_base_url = patients_attachments_url,
  onChange,
  accept = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv",
  multiple = true,
  className = "",
}) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");

  const allowedExt = accept
    .split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

  const validateFiles = (incoming) => {
    const selected = Array.from(incoming || []);
    const invalid = selected.filter((file) => {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      return !allowedExt.some((allowed) => allowed === `.${ext}` || allowed === ext || allowed === "*/*");
    });
    return { selected, invalid };
  };

  const handleFiles = (incoming) => {
    const { selected, invalid } = validateFiles(incoming);
    if (invalid.length) {
      setError("Only images, PDF, Word, and Excel/CSV files are supported.");
      return;
    }
    setError("");
    onChange?.(
      multiple
        ? [...files, ...selected]
        : selected.slice(0, 1)
    );
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index) => {
    const removedFile = files[index];

    const next = files.filter((_, idx) => idx !== index);

    onChange?.(next, removedFile);
  };

  return (
    <div className={className}>
      {label && <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-200">{label}</label>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer ${dragging ? "border-green-400 bg-green-50 dark:bg-green-500/10" : "border-gray-300 dark:border-slate-600 bg-gray-50 dark:bg-slate-800/60 hover:border-green-300 dark:hover:border-green-500/50"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className="p-4">
          {!isForDetail && <div className="flex items-center justify-between gap-3 text-sm text-gray-600 dark:text-gray-300">
            <span>Drag & drop files here or click to browse</span>
            <span className="rounded-full bg-green-100 dark:bg-green-500/10 px-2.5 py-1 text-[11px] font-semibold text-green-700 dark:text-green-300">Supports images, PDF, DOC, XLS, CSV</span>
          </div>}
          {files.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {files.map((file, idx) => {
                const kind = getFileKind(file);
                const filePreviewURL = file?.attachment_file ? `${attachment_base_url}/${file?.attachment_file}` : "";
                const preview = kind === "image" && file?.preview ? file.preview : null;
                return (
                  <div key={`${file?.attachment_file ?? file.name}-${idx}`} className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900/80 p-3 shadow-sm hover:shadow-md transition">
                    <div className="flex items-start gap-3">
                      {preview || kind === "image" && filePreviewURL ? <Img src={preview || filePreviewURL} alt={file?.attachment_file ?? file.name} className="h-16 w-16 rounded-lg object-contain" /> : <AttachmentIcon kind={kind} />}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{file.name || file.filename || file?.attachment_file || "Attachment"}</p>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{kind}</p>
                      </div>
                      {filePreviewURL && <><button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); window.open(filePreviewURL, "_blank"); }}
                        className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-blue-500 hover:bg-blue-100 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300"
                        title="Preview File"
                      >
                        <EyeIcon size={20} height={25} width={10} />
                      </button></>}
                      {!isForDetail && <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                        className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-red-500 hover:bg-red-100 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
                        title="Remove attachment"
                      >
                        <Trash2Icon size={20} height={25} width={10} />
                      </button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {error && <p className="mt-2 text-xs text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

export const FormSelect = ({
  label,
  value,
  onChange,
  options = [],
  required = false,
  isRequired = false,
  isClearable = true,
  className = "",
  error = "",
  ...props
}) => {
  const handleChange = (option) => {
    const val = option?.value ?? null;
    onChange?.(val);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-200">
        {label}
        {required || isRequired && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
      </label>
      <ReactSelect
        options={options}
        isClearable={isClearable}
        onChange={handleChange}
        value={options.find((o) => o.value === value) ?? null}
        styles={CustomSelectStyle}
        {...props}
      />
      {error && (
        <p className="mt-1 text-xs text-red-500 dark:text-red-400 flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-red-500 inline-block flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const FormSwitch = ({
  label = "Status",
  checked = false,
  onChange,
  disabled = false,
  className = "",
  when = { active: "Active", inActive: "Inactive" },
}) => {
  const status = checked ? when.active : when.inActive;
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {label}
        </label>
      )}
      <div>
        <StatusPill
          value={status}
          variant={checked ? "success" : "neutral"}
          flicker={checked ? {} : undefined}
          disabled={disabled}
          onClick={() => onChange?.(!checked)}
          role="switch"
          aria-checked={checked}
        />
      </div>
    </div>
  );
};

export const FormSection = ({ children, title, className = "" }) => (
  <div className={`space-y-4 ${className}`}>
    {title && (
      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 border-b border-green-200 dark:border-green-500 pb-2">
        {title}
      </h3>
    )}
    {children}
  </div>
);

export const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-disabled={disabled}
    onClick={() => {
      if (disabled) return;
      onChange(!checked);
    }}
    className={
      "relative inline-flex h-6 w-11 items-center rounded-full transition focus:outline-none " +
      (checked ? "bg-green-500 dark:bg-green-500" : "bg-gray-200 dark:bg-slate-700")
    }
  >
    <span
      className={
        "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
        (checked ? "translate-x-6" : "translate-x-1")
      }
    />
  </button>
);